import { useEffect, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  SegmentedControl,
  Skeleton,
  Spinner,
  useToast
} from '@/components/ui'
import { errorMessage } from '@/lib/api'
import { formatNumber, pluralize } from '@/lib/format'
import { useSettingsStore } from '@/stores/useSettingsStore'

/** The API clamps `concurrency` to 1–8, so the picker offers exactly that range. */
const MAX_USER_CONCURRENCY = 8

const CHOICES = Array.from({ length: MAX_USER_CONCURRENCY }, (_, index) => ({
  value: String(index + 1),
  label: String(index + 1)
}))

type SaveState = 'idle' | 'saving' | 'saved'

export default function ConcurrencyCard(): React.ReactElement {
  const settings = useSettingsStore((state) => state.settings)
  const loading = useSettingsStore((state) => state.loading)
  const queue = useSettingsStore((state) => state.status?.queue ?? null)
  const save = useSettingsStore((state) => state.save)
  const loadStatus = useSettingsStore((state) => state.loadStatus)
  const { toast } = useToast()
  const [saveState, setSaveState] = useState<SaveState>('idle')

  // The instance cap is the whole point of the copy below, so it is fetched here
  // rather than assumed to have been loaded by another card on the page.
  useEffect(() => {
    if (queue === null) void loadStatus()
  }, [queue, loadStatus])

  useEffect(() => {
    if (saveState !== 'saved') return
    const timer = window.setTimeout(() => setSaveState('idle'), 2500)
    return () => window.clearTimeout(timer)
  }, [saveState])

  const mine = settings?.concurrency ?? null

  // `globalConcurrency` is the whole in-flight image pool, and the pool is shared
  // rather than divided between the user slots — so it is also the ceiling on one
  // user, who can fill it alone when nobody else is generating. Dividing it by
  // `maxConcurrentUsers` would under-report the cap by the number of slots.
  const perUserCap = queue !== null ? Math.max(1, queue.globalConcurrency) : null
  const clamped = mine !== null && perUserCap !== null && mine > perUserCap
  const effective = mine !== null && perUserCap !== null ? Math.min(mine, perUserCap) : mine

  const change = async (value: string): Promise<void> => {
    const next = Number(value)
    if (!Number.isInteger(next) || next < 1 || next > MAX_USER_CONCURRENCY) return
    if (next === settings?.concurrency) return

    setSaveState('saving')
    try {
      await save({ concurrency: next })
      setSaveState('saved')
    } catch (error) {
      setSaveState('idle')
      toast({
        title: 'Could not save your concurrency',
        description: errorMessage(error),
        variant: 'error'
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generation concurrency</CardTitle>
        <CardDescription>
          How many of your images Argus works on at the same time when you generate alt text.
        </CardDescription>
      </CardHeader>

      <CardBody className="space-y-4">
        {!settings && loading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-72 max-w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <SegmentedControl
                label="Images at once"
                value={String(mine ?? 1)}
                options={CHOICES}
                onChange={(value) => {
                  void change(value)
                }}
              />
              <span aria-live="polite" className="text-xs font-medium text-fg-muted">
                {saveState === 'saving' ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Spinner size="sm" />
                    Saving…
                  </span>
                ) : null}
                {saveState === 'saved' ? 'Saved' : null}
              </span>
            </div>

            <p className="text-sm leading-relaxed text-fg-muted">
              Argus starts one Claude Code CLI process per image, so this is how many of your images
              can be in flight at once. Higher is not automatically faster: the CLI lives on the
              server host, and every generating user draws from one shared pool of processes there.
            </p>

            {clamped && mine !== null && perUserCap !== null ? (
              <div className="flex gap-2.5 rounded-control border border-warn-500/40 bg-warn-100/70 p-3.5 dark:bg-warn-950/50">
                <TriangleAlert
                  className="mt-0.5 size-4 shrink-0 text-warn-700 dark:text-warn-300"
                  aria-hidden="true"
                />
                <div className="min-w-0 text-xs leading-relaxed text-warn-700 dark:text-warn-300">
                  <p className="font-semibold">
                    You chose {formatNumber(mine)}, but this instance allows{' '}
                    {formatNumber(perUserCap)} {pluralize(perUserCap, 'image')} per user at a time.
                  </p>
                  <p className="mt-1">
                    Argus will run {formatNumber(perUserCap)} of your images at once and nothing
                    above the number here gets used, so raising it further changes nothing. The cap
                    is the size of the whole shared pool of CLI processes on the host, because every
                    in-flight image is one of them; an admin can lift it under Admin → Instance
                    settings → Images in flight.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-xs leading-relaxed text-fg-subtle">
                {perUserCap === null
                  ? 'This instance also caps how many images one user may run at a time, and that cap always wins — your number can never take you past it. It is shown under System status below.'
                  : `This instance runs ${formatNumber(perUserCap)} ${pluralize(perUserCap, 'image')} at a time in total, shared between everyone generating. Yours is set to ${formatNumber(effective ?? 1)}, which fits inside that — you get the whole pool when nobody else is working, and a round-robin share when they are. See System status below.`}
              </p>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

export { ConcurrencyCard }
